import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useStartAdSignIn } from '@/hooks/useAds'

interface ConnectGoogleAdsButtonProps {
  /** The venue the sign-in starts from; its new accounts are offered to it. */
  venueId?: string
  /** An outline button where the page already has its primary action. */
  quiet?: boolean
}

/** Sends the person to Google's own sign-in screen. Shown with {@link GoogleSignInNote}. */
export function ConnectGoogleAdsButton({ venueId, quiet = false }: ConnectGoogleAdsButtonProps) {
  const start = useStartAdSignIn()
  const { toast } = useToast()
  const leaving = start.isPending || start.isSuccess

  function connect() {
    start.mutate(venueId === undefined ? {} : { venueId }, {
      onSuccess: ({ url }) => window.location.assign(url),
      onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }),
    })
  }

  return (
    <Button variant={quiet ? 'outline' : 'default'} size={quiet ? 'sm' : 'default'} onClick={connect} disabled={leaving}>
      {leaving ? 'Opening Google…' : 'Connect Google Ads'}
    </Button>
  )
}

interface GoogleSignInNoteProps {
  /** On a venue's page, the login to use is the venue's own. */
  forVenue?: boolean
}

/**
 * What Google is about to ask, said before anyone goes there: Google offers no
 * read-only permission, so its screen asks to manage the account
 * (docs/ad-analytics.spec.md, "Read only"). And which login to use — on the
 * company's page a shared one, so the connection doesn't leave with a person.
 */
export function GoogleSignInNote({ forVenue = false }: GoogleSignInNoteProps) {
  return (
    <div className="max-w-prose space-y-1 text-sm text-ink-muted">
      <p className="m-0">
        You’ll sign in on Google’s own page. Google asks for permission to manage the account; FLOH Venues only reads
        it and never changes anything in Google Ads.
      </p>
      <p className="m-0">
        {forVenue
          ? 'Sign in with the Google login that has this venue’s own Google Ads account.'
          : 'Sign in with a shared company Google login that has your manager account, so the connection doesn’t depend on one person. Use a venue’s own login for any account kept separately.'}
      </p>
    </div>
  )
}
